import { ChevronRight, type LucideIcon } from 'lucide-react';
import { useId, useState, type ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { hairline, panel, toneTint, type SurfaceTone } from '@/components/surface/tones';

/**
 * The small visual vocabulary the Backups page shares.
 *
 * ## Colour is never the only channel
 *
 * Every status drawn here carries an icon or a word as well as a hue: a tick and a cross differ in
 * shape, and a state is always also written out. Roughly one man in twelve cannot tell red from
 * green, and the failure is the thing they most need to see.
 *
 * ## Surfaces, not outlines
 *
 * Nothing here draws a border to separate itself. Panels are set off by their own surface and a
 * soft shadow (see `panel` in tones.ts), rows by a hairline or by space, and pills, chips and
 * glyphs by a tint.
 */

export function Panel({
  id,
  className,
  children,
  label,
}: {
  id?: string | undefined;
  className?: string | undefined;
  children: ReactNode;
  /** For a panel with no visible heading of its own. */
  label?: string | undefined;
}) {
  return (
    <section id={id} aria-label={label} className={cn(panel, 'min-w-0 scroll-mt-24', className)}>
      {children}
    </section>
  );
}

/**
 * Title, one quiet line under it, and the section's own actions on the right.
 *
 * The icon sits on a small tile tinted by what the section is about, which gives each panel a point
 * of colour to find it by — in the light theme a grey glyph on white was the flattest thing on it.
 */
export function SectionHeader({
  icon: Icon,
  iconTone = 'muted',
  title,
  description,
  badge,
  actions,
}: {
  icon?: LucideIcon | undefined;
  iconTone?: SurfaceTone;
  title: string;
  description?: ReactNode;
  badge?: ReactNode;
  actions?: ReactNode;
}) {
  // The description runs the full width under the title row, so a narrow panel with a button in its
  // header does not squeeze its one line of explanation into a column beside the button.
  return (
    <div className="px-5 pb-3 pt-4">
      <div className="flex min-h-8 items-center justify-between gap-3">
        <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
          {Icon && (
            <span aria-hidden className={cn('mr-0.5 inline-flex size-7 shrink-0 items-center justify-center rounded-md', toneTint[iconTone])}>
              <Icon className="size-4" />
            </span>
          )}
          <h2 className="text-[15px] font-semibold leading-6 tracking-tight">{title}</h2>
          {badge}
        </div>
        {actions !== undefined && <div className="-mr-2 flex shrink-0 items-center gap-1.5">{actions}</div>}
      </div>
      {description !== undefined && <p className="text-[13px] text-muted-foreground">{description}</p>}
    </div>
  );
}

/** A tinted rounded square holding an icon, for a resource in a list. */
export function IconTile({ icon: Icon, tone = 'primary' }: { icon: LucideIcon; tone?: SurfaceTone }) {
  return (
    <span aria-hidden className={cn('inline-flex size-8 shrink-0 items-center justify-center rounded-lg', toneTint[tone])}>
      <Icon className="size-4" />
    </span>
  );
}

/** A status as a round glyph, for the leading edge of a row. */
export function StatusGlyph({
  icon: Icon,
  tone,
  spin = false,
  label,
  size = 'md',
}: {
  icon: LucideIcon;
  tone: SurfaceTone;
  spin?: boolean;
  /** Read out in place of the icon, which is decorative on its own. */
  label: string;
  size?: 'sm' | 'md' | 'lg';
}) {
  return (
    <span
      role="img"
      aria-label={label}
      title={label}
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-full',
        size === 'sm' ? 'size-6' : size === 'lg' ? 'size-10' : 'size-8',
        toneTint[tone],
      )}
    >
      <Icon className={cn(size === 'sm' ? 'size-3.5' : size === 'lg' ? 'size-5' : 'size-4', spin && 'animate-spin')} aria-hidden />
    </span>
  );
}

/** A state written as a word with a dot: Active, Paused, Not running, All passing. */
export function StatusPill({ tone, label, pulse = false }: { tone: SurfaceTone; label: string; pulse?: boolean }) {
  return (
    <span className={cn('inline-flex shrink-0 items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium', toneTint[tone])}>
      <span className="relative flex size-1.5" aria-hidden>
        {pulse && <span className="absolute inline-flex size-full animate-ping rounded-full bg-current opacity-60" />}
        <span className="relative inline-flex size-1.5 rounded-full bg-current" />
      </span>
      {label}
    </span>
  );
}

/** A tinted label with an optional icon: Offsite, Restore tested, Latest. */
export function Pill({
  tone,
  icon: Icon,
  children,
  title,
}: {
  tone: SurfaceTone;
  icon?: LucideIcon | undefined;
  children: ReactNode;
  title?: string | undefined;
}) {
  return (
    <span title={title} className={cn('inline-flex shrink-0 items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] font-medium leading-4', toneTint[tone])}>
      {Icon && <Icon className="size-3" aria-hidden />}
      {children}
    </span>
  );
}

/**
 * A section that opens in place: detail kept one click away rather than on the page by default.
 *
 * A real disclosure — a button that says whether it is open, controlling the region it reveals.
 */
export function Disclosure({
  title,
  meta,
  defaultOpen = false,
  children,
}: {
  title: string;
  meta?: ReactNode;
  defaultOpen?: boolean;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const id = useId();

  return (
    <div className={cn('border-t', hairline)}>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen((value) => !value)}
        className="flex w-full items-center gap-2 px-5 py-3 text-left text-[13px] font-medium text-foreground/90 transition-colors hover:bg-foreground/[0.03]"
      >
        <ChevronRight className={cn('size-3.5 shrink-0 text-muted-foreground transition-transform', open && 'rotate-90')} aria-hidden />
        <span className="flex-1">{title}</span>
        {meta !== undefined && <span className="text-xs font-normal text-muted-foreground">{meta}</span>}
      </button>
      {open && (
        <div id={id} className="pb-3">
          {children}
        </div>
      )}
    </div>
  );
}

/** A placeholder block while the overview loads. */
export function Skeleton({ className }: { className?: string }) {
  return <div aria-hidden className={cn('animate-pulse rounded-xl bg-foreground/[0.05]', className)} />;
}

/**
 * Stands in for a figure whose query has not answered yet: a short pulse inside the line of text.
 * Never a dash, which reads as a placeholder, and never a zero, which reads as a fact.
 */
export function PendingValue({ className }: { className?: string }) {
  return (
    <span className={cn('inline-block h-[0.8em] w-10 animate-pulse rounded bg-foreground/[0.08] align-middle', className)}>
      <span className="sr-only">Loading</span>
    </span>
  );
}
